import gql from "graphql-tag";
import queries from "../gql/queries";
import axios from "axios";
import { getTotalPrice } from "./PricingCalculator";
import { renderAddress } from "./";

let orderData = {};
// let originalItemCount = -1;
let computedSubtotal = 0;
let computedTaxRate = 0;
let addresses = [];
let apolloClient;
let thisContext;

// Define helper functions

/**
 * Fetches order information and returns the constructed orderData object.
 * @param {String} orderId - The ID of the order to fetch.
 * @returns {Object} orderData - The constructed orderData object.
 */
async function getOrderInfo(orderId, apollo, context) {
	apolloClient = apollo;
	thisContext = context;
	try {
		//create gql filter request to get orderData by id
		console.log("getting order data...", orderId);

		const res = await apolloClient.query({
			query: queries.GET_SALES_ORDER,
			variables: {
				id: orderId,
			},
			fetchPolicy: "no-cache",
		});

		const order = res.data.salesOrderById;

		orderData = {
			...order,
			salesOrderId: order.salesOrderId,
			organization: order.customer.customer.organization,
			createdAt: order.createdAt.split("T").shift(),
			billToId: order.customer.billTo,
			shipToId: order.customer.shipTo,
			contactId: order.customer.contactId,
			salesTaxId: order.customer.customer.salesTaxId ?? 0,
			customerId: order.customer.customer.customerId,
			customerNumber: order.customer.customer.customerNumber,
			quoteNumber: order.quote.quoteNumber,
			items: [],
			notes: order.quote.notes ? order.quote.notes : [],
			payments: order.quote.payments ? order.quote.payments : [],
			orderDate: order.orderDate.split("T").shift(),
			shippingAndHandling: order.shippingAndHandling,
			shippingAndHandlingDiscount: order.shippingAndHandlingDiscount,
			balance: order.balance,
		};

		addresses = order.customer.customer.addresses;

		if (order.status === "IN_PROCESS") {
			orderData.status = "IN PROCESS";
		}

		await getOrderItems(orderId);
		await getTaxRate(computedZipcodeState()[0], computedZipcodeState()[1]);
		return orderData;
	} catch (error) {
		console.log(error);
		return {};
	}
}

/**
 * Fetches order items associated with the given orderId.
 * @param {String} orderId - The ID of the order to fetch items for.
 * @returns {Array} items - The array of order items.
 */
async function getOrderItems(orderId) {
	try {
		const res = await axios.get(`/api/orders/${orderId}/lineItems`);
		console.log("getOrderItems", res);

		const lpRes = await apolloClient.query({
			query: gql`
				query {
					letterPosition(take: 500) {
						items {
							letterPositionId
							code
							description
						}
					}
				}
			`,
			fetchPolicy: "no-cache",
		});

		const letterPositions = lpRes.data.letterPosition.items;

		//loop through all items and get there size and color option data in parellel async
		const orderItems = await Promise.all(
			res.data.lineItems.map(async (item) => {
				//get items size chart and color options from its original inventory item
				let inventoryItem = await getLineItemSizeColorData(item);
				item = { ...item, ...inventoryItem };
				updateColorOptionsForTableData(item);

				if (item.children && item.children.length > 0) {
					//if item has children loop through the level 2 items and get there color and size data
					const orderChildItems = await Promise.all(
						item.children.map(async (childItem) => {
							let inventoryItem = await getLineItemSizeColorData(childItem);
							childItem = { ...childItem, ...inventoryItem };
							childItem.priceIncluded = childItem.includePrice;
							updateColorOptionsForTableData(childItem);

							if (childItem.lettering) {
								let letterPositionById = letterPositions.find(
									(s) => s.letterPositionId == childItem.lettering.letterPositionId
								);
								childItem.lettering = { ...childItem.lettering, ...letterPositionById };
							}

							if (childItem.children && childItem.children.length > 0) {
								//if item has children loop through the level 3 items and get there color and size data
								const orderSubChildItems = await Promise.all(
									childItem.children.map(async (sub) => {
										let inventoryItem = await getLineItemSizeColorData(sub);
										sub = { ...sub, ...inventoryItem };
										sub.priceIncluded = sub.includePrice;
										updateColorOptionsForTableData(sub);

										return sub;
									})
								);
								childItem.children = orderSubChildItems;
							}

							return childItem;
						})
					);
					item.children = orderChildItems;
				}

				return item;
			})
		);

		orderData.items = orderItems;
		computedSubtotal = getTotalPrice(orderItems, 0).toFixed(2);
		setPricesForPrint();
	} catch (error) {
		console.log(error);
	}
}

async function getLineItemSizeColorData(item) {
	try {
		let res;
		if (thisContext && thisContext.$systemCategories.isSubItem(item.systemCategoryId)) {
			//if a subitem is passed in we only need colorOptions
			res = await apolloClient.query({
				query: gql`
					query GetSubItemById($id: Int!) {
						subItemById(id: $id) {
							colorOptions {
								colorOptionId
								name
								description
								position
								type
								colorPalettes {
									name
									description
									upcharge
									colors {
										label
										description
										hexCode
										isVisible
										colorId
										isRetired
									}
								}
							}
						}
					}
				`,
				fetchPolicy: "no-cache",
				variables: {
					id: item.itemId,
				},
			});

			console.log("getting inventory subItem color data", res);
			res.data.subItemById.type = "SubItem";
			return res.data.subItemById;
		} else {
			//if a regular inventory item is passed in we need to get color and size data
			res = await apolloClient.query({
				query: gql`
					query GetInventoryItemById($id: Int!) {
						inventoryItemById(id: $id) {
							colorOptions {
								colorOptionId
								name
								description
								position
								type
								colorPalettes {
									name
									description
									upcharge
									colors {
										label
										description
										hexCode
										isVisible
										colorId
										isRetired
									}
								}
							}
							sizeChart {
								name
								description
								includeLengths
								sizes {
									lengths {
										length
										sizeId
										sizeLengthId
										isRetired
									}
									name
									showSize
									sizeId
									isRetired
									sequence
								}
							}
						}
					}
				`,
				fetchPolicy: "no-cache",
				variables: {
					id: item.itemId,
				},
			});

			console.log("getting inventory item size and color data", res);
			return res.data.inventoryItemById;
		}
	} catch (error) {
		console.log(error);
	}
}

function updateColorOptionsForTableData(item) {
	// If the item's colorOptions property is empty or undefined, update it
	if (
		(!item.colorOptions || item.colorOptions.length === 0) &&
		item.colorSelections &&
		item.colorSelections.length > 0
	) {
		// Create a colorOption for each colorSelection
		item.colorOptions = item.colorSelections.map((selection, index) => {
			return {
				colorOptionId: index, // Using index as ID. Adjust if needed.
				name: "",
				description: "",
				position: index + 1, // Using index + 1 for position
				colorPalettes: [
					{
						name: "Default",
						description: "Default",
						colors: [
							{
								label: selection.colorName,
								description: selection.colorName,
								hexCode: selection.hexCode,
								isVisible: true,
								colorId: selection.colorId,
								isRetired: false,
							},
						],
					},
				],
			};
		});
	} else if (item.colorOptions && item.colorSelections) {
		// For each colorSelection, check if a corresponding colorOption exists
		item.colorSelections.forEach((selection) => {
			const matchingOption = item.colorOptions.find(
				(option) =>
					option.colorPalettes &&
					option.colorPalettes[0] &&
					option.colorPalettes[0].colors &&
					option.colorPalettes[0].colors.find((color) => color.colorId === selection.colorId)
			);

			// If no matching colorOption is found, add the colorSelection to the first colorOption's colors list
			if (
				!matchingOption &&
				item.colorOptions[0] &&
				item.colorOptions[0].colorPalettes &&
				item.colorOptions[0].colorPalettes[0]
			) {
				const newColor = {
					label: selection.colorName,
					description: selection.colorName,
					hexCode: selection.hexCode,
					isVisible: true,
					colorId: selection.colorId,
					isRetired: false,
				};

				// Add the new color to the first colorOption's colors list
				item.colorOptions[0].colorPalettes[0].colors.push(newColor);
			}
		});
	}
}

function setPricesForPrint() {
	let taxAmount =
		orderData.salesTaxId === 0 ? 0 : parseFloat((computedTaxRate * computedSubtotal) / 100);

	let printPrices = {
		computedSubtotal: computedSubtotal,
		shippingAndHandling: orderData.shippingAndHandling,
		shippingAndHandlingDiscount: orderData.shippingAndHandlingDiscount,
		computedTaxRate: computedTaxRate,
		computedTaxAmount: taxAmount,
		computedOrdertotal: computedOrdertotal(),
		computedPayments: computedPayments(),
		computedBalance: computedBalance(),
	};

	orderData.printPrices = printPrices;
}

function computedShipToAddr() {
	//filter out any customer addresses that are not labeled ship to or both
	return addresses
		.filter(
			(addr) => addr.label.toLowerCase() == "ship to" || addr.label.toLowerCase() == "both"
		)
		.map((obj) => ({ ...obj, text: renderAddress(obj) }));
}

function computedZipcodeState() {
	// Use Ship To address for tax calculation (tax is based on destination)
	const selectedTaxObj = computedShipToAddr().find((el) => el.addressId == orderData.shipToId);
	const zipCode = selectedTaxObj?.postalCode;
	const state = selectedTaxObj?.state;
	getTaxRate(zipCode, state);
	return [zipCode, state];
}

function computedOrdertotal() {
	let orderTotal = 0.0;

	orderTotal =
		parseFloat(computedSubtotal ?? 0) +
		parseFloat(orderData.shippingAndHandling ?? 0) -
		parseFloat(orderData.shippingAndHandlingDiscount ?? 0) +
		(orderData.salesTaxId === 0 ? 0 : parseFloat((computedTaxRate * computedSubtotal) / 100));

	return orderTotal.toFixed(2);
}

function computedPayments() {
	let payments = 0.0;

	if (orderData.payments && orderData.payments.length > 0) {
		orderData.payments.forEach((payment) => (payments += parseFloat(payment.amount)));
	}

	return payments.toFixed(2);
}

function computedBalance() {
	let balance = 0.0;

	balance += computedOrdertotal() - computedPayments();

	return balance.toFixed(2);
}

async function getTaxRate(zipCode, state) {
	if (zipCode && state) {
		const { data } = await apolloClient.query({
			query: gql`
				query GetZipCodeTaxRate($zipCode: String!, $state: String!) {
					zipCodeTaxRates(
						where: { and: [{ zipCode: { eq: $zipCode } }, { state: { eq: $state } }] }
					) {
						items {
							zipCode
							state
							combinedRate
						}
					}
				}
			`,
			variables: {
				zipCode,
				state,
			},
		});
		computedTaxRate = data.zipCodeTaxRates?.items?.[0]?.combinedRate || 0;
	} else computedTaxRate = 0;
}

export { getOrderInfo };
